import type { FC } from 'react';
import React, { useRef, useState, useEffect, startTransition } from 'react';

import { useAudioBriefingChangeboarding } from '@confluence/audio/entry-points/useAudioBriefingChangeboarding';
import { expVal } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';

import { AudioButtonLoaderActivatable, AudioButtonLoaderAfterPaint } from './AudioButtonLoader';

type AudioButtonHandle = {
	open: () => void;
};

type AudioButtonLoaderWrapperProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
	isDisabled?: boolean;
	isView: boolean;
};

export const AudioButtonLoaderWrapper: FC<AudioButtonLoaderWrapperProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
	isDisabled,
	isView,
}) => {
	const audioButtonRef = useRef<AudioButtonHandle | null>(null);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	const [audioSupportedByBrowser, setAudioSupportedByBrowser] = useState<boolean | null>(true); // assume audio is enabled for SSR until client-side useEffect checks browser support
	const [isFocusActivated, setIsFocusActivated] = useState(false);

	useEffect(() => {
		const isAudioSupportedByBrowser =
			typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg');
		if (!isAudioSupportedByBrowser) {
			startTransition(() => {
				setAudioSupportedByBrowser(false);
			});
		}
	}, []);

	useEffect(() => {
		// focus listener must live outside AudioButton code
		const wrapper = wrapperRef.current;
		if (!wrapper) {
			return;
		}
		const handleFocus = (event: FocusEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || !target.matches('button, [role="button"]')) {
				return;
			}
			const isKeyboardFocus = target.matches(':focus-visible');
			if (!isKeyboardFocus) {
				return;
			}
			startTransition(() => {
				setIsFocusActivated(true);
			});
		};
		wrapper.addEventListener('focus', handleFocus, true);
		return () => {
			wrapper.removeEventListener('focus', handleFocus, true);
		};
	}, []);

	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { showChangeboarding, handleCloseChangeboarding } = useAudioBriefingChangeboarding({
		contentId,
		onShow: () => {
			audioButtonRef.current?.open();
		},
		skip: audioSupportedByBrowser === false, // skip until supported browser check comes back
	});

	const isButtonDisabled =
		(isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)) ||
		(!renderServerPlaceholder && !audioSupportedByBrowser);

	// only immediately hydrate if:
	// 1) changeboarding is enabled and the spotlight needs to be activated on page load OR
	// 2) audio button is not supported by browser (firefox) and component needs to hydrate to show as disabled on page load
	// 3) user activates the button via keyboard focus
	const immediateActivationCondition =
		showChangeboarding || audioSupportedByBrowser === false || isFocusActivated;

	return (
		<div ref={wrapperRef}>
			{isView && expVal('confluence_selective_hydration_audio_button', 'isEnabled', false) ? (
				<AudioButtonLoaderActivatable
					audioButtonRef={audioButtonRef}
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					contentTitle={contentTitle}
					isDisabled={isButtonDisabled}
					showChangeboarding={showChangeboarding}
					handleCloseChangeboarding={handleCloseChangeboarding}
					audioSupportedByBrowser={audioSupportedByBrowser ?? true}
					immediateActivationCondition={immediateActivationCondition}
				/>
			) : (
				<AudioButtonLoaderAfterPaint
					audioButtonRef={audioButtonRef}
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					contentTitle={contentTitle}
					isDisabled={isButtonDisabled}
					showChangeboarding={showChangeboarding}
					handleCloseChangeboarding={handleCloseChangeboarding}
					audioSupportedByBrowser={audioSupportedByBrowser ?? true}
				/>
			)}
		</div>
	);
};
